// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
Men's antique jewelry is the quietest corner of the market, which is exactly why it is the interesting one. The workmanship is often as good as anything made for women, the survival rate is lower because so much was melted, and the prices have not caught up.

## The categories

**Cufflinks.** The obvious one, and the most collected. Two decorated faces joined by a link, a chain, or a swivel bar.

**Dress studs.** Sets for a stiff evening shirt front, usually mother of pearl, onyx or enamel, often boxed with matching cufflinks and waistcoat buttons.

**Tie pins, also called stick pins or scarf pins.** A single long pin with a decorative head. Victorian and Edwardian men wore them constantly, and they are among the cheapest ways into antique jewelry.

**Signet rings.** Historically a working seal, later a mark of family. Frequently gypsy set or engraved with a crest.

**Watch chains: Alberts and double Alberts.** A chain across the waistcoat with a T-bar through a buttonhole. Double Alberts have two drops, one for the watch and one for a fob or a seal.

**Fobs and seals.** Hung from the chain: carved stone seals, spinning fobs, vesta cases, sovereign holders.

**Collar studs, shirt studs and hat pins.** The everyday hardware of dressing before the twentieth century.

## Cufflinks: what to look at

**The connection type dates them.** Chain link cufflinks, where two faces are joined by a short chain, are the older form and dominate the nineteenth century into the early twentieth. Rigid bar and swivel backs, where a hinged bar flips to pass through the cuff, become common from around the 1920s and are standard by mid century.

**Check the swivel.** A swivel that no longer holds at ninety degrees is worn. It is repairable, but it is the most common fault.

**Check the join between face and post.** All the leverage happens there. Look for a hairline crack or a solder repair.

**Both faces should match.** On antique pairs, faces should be a genuine pair, not merely similar. Compare the engraving, the wear pattern and the weight.

**Look for a mark on the back or the bar.** Cufflinks are frequently marked where nothing shows in wear.

## Dress sets, and why a complete one is worth more

A boxed set of cufflinks plus studs, in its original fitted case, is worth considerably more than the sum of its parts. Boxes get lost and studs get scattered, so a complete set with its box is genuinely uncommon.

If you are buying a partial set, check that everything matches in colour and wear. Mixed sets, assembled later from odd pieces, are common and should be described as such.

## Tie pins are the cheap way in

A tie pin has one decorated head, one pin, and almost no metal. That makes them affordable even when the head is good work: a small enamel, a seed pearl cluster, a carved stone, a horseshoe with tiny diamonds.

Two things to check:

- **Is the pin straight, and does it still have its point?** Bent and blunted pins are common, and straightening an old pin risks snapping it.
- **Is there a protective sleeve?** Rarely still present, easily replaced.

Tie pins also convert well into pendants and brooches, which is a mixed blessing: it is a good use for a damaged pin, and it means many surviving heads have already lost their pins.

## Watch chains

Albert chains are sold by weight as much as by design, and weight is the number to ask for. A solid gold Albert is a substantial piece of metal and its scrap floor is real, which our [resale guide](/blog/how-much-is-my-jewelry-worth) explains how to calculate.

Check:

- **The T-bar** is present and matches.
- **The swivel clip** closes properly, since this is what held a valuable watch.
- **Solid or hollow links.** Hollow Alberts exist and are worth far less. Compare weight to size.
- **Marks.** British chains are often marked on individual links, which is a strong sign of quality and makes dating easy.

Our [chain guide](/blog/necklace-lengths-and-chain-types) covers link types and how to spot hollow construction.

## Signet rings, briefly

The engraving is the value. A crisply cut crest or monogram by a hand engraver is worth having; a worn-flat face is a blank waiting for new work.

Two questions matter. Is the face deep enough to re-engrave, if you want your own mark? And do you mind removing someone else's? Recutting an antique signet is completely normal, but it does erase a piece of history, and on a fine early example it lowers value.

Signets are usually [gypsy set](/blog/ring-settings-explained) if they carry a stone, which is why so many survive in wearable condition.

## What men's pieces are worth, and why they are undervalued

Three things suppress prices in this corner:

1. **Melt.** Enormous quantities of Victorian and Edwardian men's gold were scrapped in the gold price spikes of the twentieth century.
2. **Fashion.** Waistcoats, stiff collars and detachable cuffs went out, and the objects went with them.
3. **A smaller audience.** Fewer buyers means slower sales and softer prices.

For a collector that is an opportunity. You can still buy hand engraved, hallmarked, solid gold work from the 1890s for less than a modern mass produced equivalent costs new.

## Buying and selling men's antique jewelry

If you are **buying**, ask for the weight, a photograph of any mark, and a clear photograph of the mechanism: the swivel, the pin, the clip. Mechanisms are where these pieces fail, and they are rarely photographed unless you ask. Our [guide to spotting reproductions](/blog/how-to-spot-fake-antique-jewelry) covers what else the back tells you.

If you are **selling**, say which category it is in the title. Buyers search for cufflinks, stick pins, Albert chains and signet rings by name, not for men's jewelry in general. Our [guide to writing a listing](/blog/how-to-write-a-jewelry-listing-that-sells) covers the rest.

On Nextmine the buyer's payment is held until the piece has arrived and been confirmed. [See what is listed](/s), or read [how payments work](/how-payments-work).
`;

export default body;
